import React from "react";
import { createRoot } from "react-dom/client";
import "@fortawesome/fontawesome-free/css/fontawesome.min.css";
import "@fortawesome/fontawesome-free/css/solid.min.css";
import "@fortawesome/fontawesome-free/css/brands.min.css";
import "./index.css";
import "./animations.css";
import "./styles/DesignSystem.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import ReactErrorBoundary from "./components/Discord/ErrorBoundary/ReactErrorBoundary";
import { isStaleAssetFailure } from "./utils/assetPolicy";
import {
  markAssetRecoveryHealthy,
  recoverFromStaleAssets,
  showAssetRecoveryFallback,
} from "./utils/assetRecovery";
import { logError } from "./utils/errorLogger";

// Prevent right-click context menu on images
document.addEventListener('contextmenu', (e) => {
  if (e.target.tagName === 'IMG') {
    e.preventDefault();
    return false;
  }
}, false);

// Prevent drag and drop of images
document.addEventListener('dragstart', (e) => {
  if (e.target.tagName === 'IMG') {
    e.preventDefault();
    return false;
  }
}, false);

function describeAssetFailure(event) {
  const target = event.target;
  const reason = event.reason;
  const targetUrl = target && target !== window ? target.src || target.href || '' : '';
  return {
    message: event.message || (reason && reason.message) || '',
    name: (event.error && event.error.name) || (reason && reason.name) || '',
    filename: event.filename || '',
    targetUrl: typeof targetUrl === 'string' ? targetUrl : '',
    targetTag: target && target.tagName ? target.tagName : '',
  };
}

function handleStaleAsset(event) {
  if (!isStaleAssetFailure(describeAssetFailure(event))) return false;
  recoverFromStaleAssets({
    sessionStorage: window.sessionStorage,
    caches: window.caches,
    getRegistrations: () =>
      'serviceWorker' in navigator
        ? navigator.serviceWorker.getRegistrations()
        : Promise.resolve([]),
    reload: () => window.location.reload(),
    showFallback: () => showAssetRecoveryFallback(document),
    hostname: window.location.hostname,
  });
  return true;
}

// Global error handler for unhandled errors
window.addEventListener('error', (event) => {
  if (handleStaleAsset(event)) return;

  logError(event.error, null, { type: 'global', filename: event.filename, lineno: event.lineno });
}, true);

window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

  if (handleStaleAsset(event)) {
    event.preventDefault();
    return;
  }

  logError(error, null, { type: 'unhandledRejection' });
  event.preventDefault(); // Prevent default browser behavior
});

// Utility function to clear all caches (useful for development)
window.clearAllCaches = async () => {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    console.log('All caches cleared:', cacheNames);
  }
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
  }
  console.log('Caches cleared. Reload the page to see changes.');
};

// Register Service Worker for PWA and caching
// In development, we still register but with less aggressive caching
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Only register service worker in production or if explicitly enabled
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    // In development, you can disable service worker by uncommenting the next line
    // if (isDevelopment) return;
    
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        console.log('SW registered: ', registration);
        
        // Function to check for updates and handle them
        const checkForUpdates = () => {
          registration.update().catch((err) => {
            console.log('SW update check failed:', err);
          });
        };
        
        // Check for updates periodically
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // New service worker available - there's an update
                  console.log('New service worker available - update detected');
                  
                  // In development, auto-reload to get latest assets
                  if (isDevelopment) {
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  } else {
                    // In production, reload to get the new version
                    // This ensures users see changes when you deploy
                    console.log('New version available - reloading to apply update');
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  }
                } else {
                  // First time installation
                  console.log('Service worker installed for the first time');
                }
              }
            });
          }
        });
        
        // Check for updates on page load (minimal bandwidth - just checks sw.js file)
        checkForUpdates();
        
        // Check for updates periodically
        // In production, check once per day (since you deploy weekly, this is sufficient)
        // This minimizes bandwidth while still detecting updates within 24 hours
        const updateInterval = isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000; // 5 min in dev, 24 hours in prod
        setInterval(checkForUpdates, updateInterval);
        
        // Also check when page becomes visible (user returns to tab after being away)
        // This is a good balance - only checks when user is actually using the site
        let lastVisibilityCheck = Date.now();
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) {
            // Only check if it's been at least 1 hour since last check (to avoid spam)
            const timeSinceLastCheck = Date.now() - lastVisibilityCheck;
            if (timeSinceLastCheck > 60 * 60 * 1000) {
              lastVisibilityCheck = Date.now();
              checkForUpdates();
            }
          }
        });
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
  
  // Listen for messages from service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'SKIP_WAITING') {
      window.location.reload();
    }
  });
}

const root = createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ReactErrorBoundary>
      <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <App />
      </BrowserRouter>
    </ReactErrorBoundary>
  </React.StrictMode>
);
markAssetRecoveryHealthy(window.sessionStorage);
reportWebVitals();
